import { Popover } from '@mantine/core';
import classNames from 'classnames';
import GaugeChart from 'react-gauge-chart';
import { useToggle } from '../../hooks';
import exp from 'constants';

type Score = 'running' | 'rising' | 'ready';

const ALL_SCORES: Score[] = ['ready', 'rising', 'running'];
interface Props {
  score: Score;
  className?: any;
}

const SCORE_TO_PERCENTAGE = {
  running: 0.15,
  rising: 0.5,
  ready: 0.85,
};

const SCORE_TO_COLOR = {
  running: '#c7d0ff',
  rising: '#ffe6be',
  ready: '#a9cbba',
};

const SCORE_DESCRIPTION = {
  running: {
    title: 'Running',
    color: SCORE_TO_COLOR['running'],
    description:
      'The company has the opportunity to strengthen its foundation to support effective scaling. By focusing on key elements such as leadership, financial management, and operational processes, it can build stronger systems and align resources to prepare for future growth.',
  },
  rising: {
    title: 'Rising',
    color: SCORE_TO_COLOR['rising'],
    description:
      'The company demonstrates strong potential and, with further development, can achieve scalable growth. By focusing on areas such as process optimization, leadership alignment, and operational readiness, it will be well-positioned for more rapid and efficient expansion.',
  },
  ready: {
    title: 'Ready',
    color: SCORE_TO_COLOR['ready'],
    description:
      'The company has a strong foundation and is well-positioned for scalable growth. While a few minor adjustments or optimizations may enhance its readiness, the business is overall in an excellent position to achieve sustainable expansion. Leadership, operations, financials, and technology are all aligned for success.',
  },
};

const Tooltip = ({ score, current }: { score: Score; current: boolean }) => {
  const [open, toggleOpen] = useToggle(false);
  return (
    <Popover
      width={'24rem'}
      position="right"
      withArrow
      shadow="md"
      opened={open}
    >
      <Popover.Target>
        <div
          className="flex gap-2 items-center"
          onMouseEnter={toggleOpen}
          onMouseLeave={toggleOpen}
        >
          <div
            className="rounded-md"
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: SCORE_DESCRIPTION[score].color,
            }}
          />
          <div
            className={classNames('text-text-secondary', {
              'font-bold': current,
            })}
          >
            {SCORE_DESCRIPTION[score].title}
          </div>
        </div>
      </Popover.Target>
      <Popover.Dropdown style={{ pointerEvents: 'none' }}>
        <p className="text-lg text-text-secondary">
          {SCORE_DESCRIPTION[score].description}
        </p>
      </Popover.Dropdown>
    </Popover>
  );
};

export const convertScore = (score: number): Score => {
  if (score < 40) return 'running';
  if (score < 70) return 'rising';
  return 'ready';
}

export const SRAScoreCard = ({
  score: _score,
  className,
}: Props): JSX.Element => {
  const score: Score = _score.toLowerCase() as Score;
  return (
    <div className={`w-full rounded-[2rem] border-[1px] p-8 my-8 ${className}`}>
      <div className="flex gap-2 pt-2 items-center">
        <div>
          <div className="flex gap-4 items-center">
            <div className="flex flex-col gap-2">
              <h2 className="text-xl text-center w-full font-bold">Your Score</h2>
            <GaugeChart
              id="gauge-chart3"
              nrOfLevels={3}
              animate={false}
              colors={['#c7d0ff', '#ffe6be', '#a9cbba']}
              percent={SCORE_TO_PERCENTAGE[score]}
              arcPadding={0}
              cornerRadius={0}
              formatTextValue={() => ''}
              textColor="black"
              needleColor={SCORE_TO_COLOR[score]}
              needleBaseColor={SCORE_TO_COLOR[score]}
              //@ts-expect-error not picking up on "needleScale" attribute being in Component
              needleScale={0.6}
            />
            </div>
            <div className="flex flex-col gap-4 pr-16">
              {ALL_SCORES.map((key: Score) => (
                <Tooltip key={key} score={key} current={key === score} />
              ))}
            </div>
          </div>
          <p className="text-text-secondary italic text-xs">
            Tip: Hover over each label in the Key Legend to learn more about
            each range.
          </p>
        </div>

        <div>
          {ALL_SCORES.map((key) => {
            if (key === score) {
              return (
                <div key={key}>
                  <strong>{SCORE_DESCRIPTION[key].title}</strong>:{' '}
                  {SCORE_DESCRIPTION[key].description}
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
};
